import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { Badge } from '@crowdex/ui/components/badge';
import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { toast } from '@crowdex/ui/components/toaster';
import { Copy, Film, MessageSquarePlus, Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FloatingTargetChip } from '@/components/desktop-shell/floating-composer-chips';
import { artifactGlyph } from '@/components/library-page/library-card';
import { useShellThreads } from '@/components/sidebar/shell-navigation';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { goBackInApp, openAppHome } from '@/lib/app-home';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { useLibraryPage } from '@/lib/navigation';
import { artifactMentionTarget } from '@/lib/side-panel/mention-targets';
import { askAgent, draftAgent } from './agent-ask';
import { AppActionLabelButton } from './app-actions';
import {
  appPanelOf,
  askAboutArtifact,
  duplicateArtifact,
  openArtifactInConversation,
  readOnlyArtifact,
  startAppConversation,
} from './app-conversation';
import { ArtifactElementComposer, focusElementComposer } from './artifact-element-composer';
import { frameOrigin, useItemOrigin, viewOrigin } from './artifact-origin';
import { ArtifactPanelHeader } from './artifact-panel-header';
import { ArtifactView } from './artifact-view';
import { artifactVersions, publishArtifactVersion, tagArtifactTemplate } from './artifacts-client';
import { FloatingComposerForm } from './floating-composer-form';
import { useOpenAppRoute } from './open-app-route';
import { PluginFolderActions } from './plugin-folder';
import {
  FrameActionButton,
  type FrameMenuItem,
  PluginFrame,
  useViewHeader,
  viewHeaderActions,
} from './plugin-frame';
import { renderLineLabel, renderOfLabel, rendersOf } from './renders';
import { useArtifactIndex } from './use-artifact-index';

/**
 * An artifact open in its type's app: the one surface for artifacts (docs/redesign-implementation-
 * plan.md §6.1). In a conversation (`panel`) it is a tab of the side panel, with the composer about
 * what is pointed at; outside one (`page`, the app's page) it fills the page, with the floating
 * composer that asks for a change in a conversation. Everywhere: "From <App>" on what a plugin
 * ships (read-only), "From: <conversation>" for one made elsewhere, Duplicate and Use in
 * conversation, and the app's actions. A file of a common viewer is framed by the host docked too;
 * a source lists its renders (⋯), a render says its source ("Render of … · v4", "Old") and Edit
 * opens the source.
 */
export function ArtifactItemView({
  artifact,
  placement,
  shownConversation = null,
  onOpen,
  onClose,
}: {
  artifact: ArtifactListingEntry;
  placement: 'panel' | 'page';
  /** In a panel: the conversation it is docked in. */
  shownConversation?: string | null;
  /** The view opened another artifact of its app (or a render, or a render's source). */
  onOpen(target: { space: string; path: string }): void;
  /** In a panel: closes its tab (the frame's Close). */
  onClose?: (() => void) | undefined;
}) {
  const workbench = useWorkbench();
  const openRoute = useOpenAppRoute();
  const threads = useShellThreads();
  const library = useLibraryPage();
  const viewHeader = useViewHeader();
  const origin = useItemOrigin()(artifact.conversation);
  const [versions, setVersions] = useState<{ revision: number; createdAt: string }[]>([]);
  const [published, setPublished] = useState(artifact.publishedRevision);
  const [template, setTemplate] = useState(artifact.template);
  const [busy, setBusy] = useState<'duplicate' | 'use' | null>(null);
  useEffect(() => setTemplate(artifact.template), [artifact]);
  useEffect(() => {
    if (artifact.space !== 'workspace' || artifact.conversation === null) return;
    let live = true;
    artifactVersions(artifact.conversation, artifact.path).then(
      (value) => live && setVersions(value),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [artifact]);
  const panel = appPanelOf(railPanels(workbench), artifact.plugin);
  const readOnly = readOnlyArtifact(artifact);
  // The view's own actions (`page.header.set`); one with the id of a type's action is that action
  // as the view offers it (Videos' Render, with its qualities), drawn once.
  const viewActions = viewHeaderActions(viewHeader.header, viewHeader.emit);
  const actions = workbench.actions.filter(
    (action) =>
      action.where === 'artifact' &&
      action.plugin === artifact.plugin &&
      (action.when?.types === undefined || action.when.types.includes(artifact.type)) &&
      !viewActions.some((offered) => offered.id === action.id),
  );
  const latest = versions.at(-1);
  // The conversation's index: this source's renders, or this render's source.
  const index = useArtifactIndex(artifact.conversation, {
    enabled: artifact.conversation !== null && artifact.space === 'workspace',
  });
  const renders = rendersOf(artifact, index.artifacts);
  const source =
    artifact.derivedFrom === undefined || artifact.derivedFrom === null
      ? null
      : (index.artifacts.find((candidate) => candidate.id === artifact.derivedFrom?.artifact) ??
        null);
  const renderItems: FrameMenuItem[] = renders.map((render) => ({
    id: `render:${render.id}`,
    label: renderLineLabel(render),
    icon: Film,
    onSelect: () => onOpen({ space: render.space, path: render.path }),
  }));
  const renderOf = artifact.derivedFrom != null && (
    <>
      <span className="type-meta shrink truncate text-muted-foreground" data-render-of>
        {renderOfLabel(artifact, source?.title ?? null)}
      </span>
      {artifact.derivedFrom.stale && (
        <Badge className="shrink-0" data-render-stale variant="outline">
          Old
        </Badge>
      )}
      <Button
        onClick={() => {
          const link = artifact.derivedFrom;
          if (link != null) onOpen({ space: artifact.space, path: link.path });
        }}
        size="sm"
        type="button"
        variant="outline"
      >
        <Pencil className="size-3.5" />
        Edit
      </Button>
    </>
  );
  // In the panel (D15): the view's variants and its pointing, from what the view offers.
  const variants = useDesktopPreviewState((state) => state.artifactVariants[artifact.id]) ?? [];
  const [variant, setVariant] = useState<string | undefined>(undefined);
  const maker =
    artifact.conversation === null
      ? null
      : (threads.find((thread) => thread.id === artifact.conversation)?.title ??
        'its conversation');
  const madeElsewhere =
    artifact.conversation !== null && artifact.conversation !== shownConversation;
  const fail = (title: string) => (error: unknown) => {
    setBusy(null);
    toast.error(title, error instanceof Error ? error.message : String(error));
  };
  const duplicate = () => {
    setBusy('duplicate');
    void duplicateArtifact(artifact, placement === 'panel' ? 'shown' : 'new').then(
      () => setBusy(null),
      fail(`Duplicate ${artifact.title}`),
    );
  };
  const putInConversation = () => {
    if (placement === 'panel') {
      draftAgent(artifactMentionTarget(artifact), shownConversation ?? undefined);
      return;
    }
    setBusy('use');
    void startAppConversation(panel?.view ?? null, { artifact }).then(
      () => setBusy(null),
      fail(`Use ${artifact.title}`),
    );
  };
  const toggleTemplate = () =>
    void tagArtifactTemplate(artifact, !template).then(
      () => setTemplate(!template),
      (error: unknown) => toast.error('Template', String(error)),
    );
  /** Up a level of its app: where it was opened from, else the app's home. */
  const back = () => {
    if (!goBackInApp()) openAppHome(artifact.plugin);
  };
  // The view's own actions (`page.header.set`); one of them primary takes the place of the type's.
  const viewPrimary = viewActions.some((action) => action.primary === true);
  /** The type's actions: full screen, the first is the bar's primary one (unless the view's is). */
  const typeActions = (where: 'panel' | 'page') =>
    actions.map((action, index) => (
      <AppActionLabelButton
        action={action}
        host={{
          ...(artifact.conversation === null ? {} : { conversation: artifact.conversation }),
          artifact: { title: artifact.title, path: artifact.path },
          ask: askAgent,
          // Docked, an app's route docks in the conversation shown; full screen, outside any
          // conversation, it is a dialog working in the artifact's.
          open: (opened) =>
            openRoute({
              plugin: opened.plugin,
              path: opened.run.open,
              in: placement === 'panel' ? opened.run.in : 'dialog',
              input: opened.run.input,
              size: opened.run.size,
              title: opened.label,
              conversation:
                placement === 'panel'
                  ? (shownConversation ?? undefined)
                  : (artifact.conversation ?? undefined),
            }),
        }}
        key={action.id}
        size="sm"
        variant={where === 'page' && index === 0 && !viewPrimary ? 'default' : 'secondary'}
      />
    ));
  const publishButton = artifact.publish &&
    latest !== undefined &&
    artifact.conversation !== null && (
      <Button
        disabled={published === latest.revision}
        onClick={() => {
          const conversation = artifact.conversation;
          if (conversation === null) return;
          void publishArtifactVersion(conversation, artifact.path, latest.revision).then(
            () => setPublished(latest.revision),
            (error: unknown) => toast.error('Publish', String(error)),
          );
        }}
        size="sm"
        type="button"
        variant="outline"
      >
        {published === latest.revision ? 'Published' : 'Publish'}
      </Button>
    );
  const controls = (
    <>
      {renderOf}
      {readOnly && (
        <Badge className="shrink-0" data-artifact-read-only variant="outline">
          From {artifact.pluginTitle}
        </Badge>
      )}
      {madeElsewhere && maker !== null && (
        <button
          className="type-meta max-w-48 shrink truncate rounded-control px-2 py-1 text-muted-foreground hover:bg-control hover:text-foreground"
          onClick={() => void openArtifactInConversation(artifact)}
          title={`Open ${maker}, where it was made`}
          type="button"
        >
          From: {maker}
        </button>
      )}
      {typeActions('panel')}
      <PluginFolderActions artifact={artifact} size="sm" />
      {publishButton}
      {artifact.duplicate !== null && artifact.space === 'workspace' && (
        <Button onClick={toggleTemplate} size="sm" type="button" variant="ghost">
          {template ? 'Unmark as template' : 'Mark as template'}
        </Button>
      )}
      {artifact.duplicate !== null && (
        <Button
          disabled={busy !== null}
          onClick={duplicate}
          size="sm"
          type="button"
          variant="outline"
        >
          <Copy className="size-3.5" />
          Duplicate
        </Button>
      )}
      <Button
        disabled={busy !== null}
        onClick={putInConversation}
        size="sm"
        type="button"
        variant="outline"
      >
        <MessageSquarePlus className="size-3.5" />
        Use in conversation
      </Button>
    </>
  );
  // Docked in a common viewer: the host's frame, as every extension's tab.
  if (placement === 'panel' && artifact.view === null) {
    const glyph = artifactGlyph(artifact, workbench);
    return (
      <PluginFrame
        controls={controls}
        icon={glyph.icon}
        iconSrc={glyph.src}
        layout="docked"
        menu={renderItems}
        onClose={onClose}
        openElsewhere={{
          label: 'Open full screen',
          onSelect: () => openAppHome(artifact.plugin, artifact.id),
        }}
        plugin={artifact.plugin}
        title={artifact.title}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1" title={`${artifact.typeTitle} · ${artifact.path}`}>
            <ArtifactView artifact={artifact} container="side" onOpenArtifact={onOpen} />
          </div>
          <ArtifactElementComposer artifact={artifact} />
        </div>
      </PluginFrame>
    );
  }
  if (placement === 'panel')
    return (
      <section aria-label={artifact.title} className="flex h-full min-h-0 flex-col">
        <ArtifactPanelHeader
          artifact={artifact}
          onPoint={variants.length > 0 ? focusElementComposer : undefined}
          onSelectVariant={setVariant}
          selectedVariant={variant}
          variants={variants}
        >
          {viewHeader.header?.status !== undefined && (
            <span className="type-meta shrink-0 text-muted-foreground" data-frame-status>
              {viewHeader.header.status}
            </span>
          )}
          {viewActions.map((action) => (
            <FrameActionButton action={action} key={action.id} />
          ))}
          {controls}
          {renderItems.length > 0 && <RendersMenu items={renderItems} />}
        </ArtifactPanelHeader>
        <div className="min-h-0 flex-1">
          <ArtifactView
            artifact={artifact}
            container="side"
            frame={{
              layout: 'docked',
              origin: viewOrigin(origin),
              action: viewHeader.action,
              setHeader: viewHeader.setHeader,
            }}
            onOpenArtifact={onOpen}
          />
        </div>
        {/* Lot 5b: a composer about the element pointed at (D15). */}
        <ArtifactElementComposer artifact={artifact} />
      </section>
    );
  // Full screen: the host's frame (R8), its bar holding the item's title, who made it, the
  // type's actions (the first one primary), and ⋯ with Duplicate, the template mark, Use in
  // conversation, Settings and Close.
  return (
    <PluginFrame
      actions={viewActions}
      backLabel={panel?.title}
      controls={
        <>
          {readOnly && (
            <Badge className="shrink-0" data-artifact-read-only variant="outline">
              From {artifact.pluginTitle}
            </Badge>
          )}
          {renderOf}
          {origin === null && madeElsewhere && maker !== null && (
            <button
              className="type-meta max-w-48 shrink truncate rounded-control px-2 py-1 text-muted-foreground hover:bg-control hover:text-foreground"
              onClick={() => void openArtifactInConversation(artifact)}
              title={`Open ${maker}, where it was made`}
              type="button"
            >
              From: {maker}
            </button>
          )}
          {typeActions('page')}
          <PluginFolderActions artifact={artifact} size="sm" />
          {publishButton}
        </>
      }
      icon={pluginIcon(panel?.icon ?? 'app')}
      iconSrc={panel?.iconSrc}
      layout="full"
      menu={[
        // A source's renders first: each opens in its viewer.
        ...renderItems,
        ...(artifact.duplicate !== null
          ? [
              {
                id: 'duplicate',
                label: 'Duplicate',
                icon: Copy,
                disabled: busy !== null,
                onSelect: duplicate,
              },
            ]
          : []),
        ...(artifact.duplicate !== null && artifact.space === 'workspace'
          ? [
              {
                id: 'template',
                label: template ? 'Unmark as template' : 'Mark as template',
                onSelect: toggleTemplate,
              },
            ]
          : []),
      ]}
      onBack={panel === undefined ? undefined : back}
      onClose={() => library.leave()}
      openElsewhere={{ label: 'Use in conversation', onSelect: putInConversation }}
      origin={frameOrigin(origin)}
      overlay={<ArtifactPageComposer artifact={artifact} />}
      plugin={artifact.plugin}
      status={viewHeader.header?.status}
      title={viewHeader.header?.title ?? artifact.title}
    >
      <div className="h-full pb-20" title={`${artifact.typeTitle} · ${artifact.path}`}>
        <ArtifactView
          artifact={artifact}
          container="main"
          frame={{
            layout: 'full',
            origin: viewOrigin(origin),
            action: viewHeader.action,
            setHeader: viewHeader.setHeader,
            back,
          }}
          onOpenArtifact={onOpen}
        />
      </div>
    </PluginFrame>
  );
}

/**
 * The floating composer of an artifact's page (D58): "Ask about this… ⌘L", a change request on
 * the item sent to a conversation, by default the one that made it (else a new one); its chip
 * offers the user's other conversations and a new one.
 */
function ArtifactPageComposer({ artifact }: { artifact: ArtifactListingEntry }) {
  const threads = useShellThreads();
  const [target, setTarget] = useState<string | null>(artifact.conversation);
  useEffect(() => setTarget(artifact.conversation), [artifact.conversation]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const title =
    target === null
      ? 'a new conversation'
      : (threads.find((thread) => thread.id === target)?.title ?? 'its conversation');
  const choices = threads
    .filter((thread) => thread.id !== target)
    .slice(0, 6)
    .map((thread) => ({ id: thread.id, title: thread.title }));
  const send = (message: string) => {
    setSending(true);
    void askAboutArtifact(artifact, message, target).then(
      () => {
        setSending(false);
        setText('');
      },
      (error: unknown) => {
        setSending(false);
        toast.error(
          `Ask about ${artifact.title}`,
          error instanceof Error ? error.message : String(error),
        );
      },
    );
  };
  return (
    <FloatingComposerForm
      className="-translate-x-1/2 absolute bottom-4 left-1/2 w-[min(720px,calc(100%-2rem))]"
      data-artifact-page-composer
      input={input}
      inputLabel="Ask about this"
      label={`Ask about ${artifact.title}`}
      leading={<FloatingTargetChip choices={choices} onChoose={setTarget} title={title} />}
      onChange={setText}
      onSend={send}
      placeholder="Ask about this…"
      sending={sending}
      value={text}
    />
  );
}

/** A source's renders, in the panel's header of an extension's view: a menu of them. */
function RendersMenu({ items }: { items: readonly FrameMenuItem[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" type="button" variant="outline">
          <Film className="size-3.5" />
          Renders · {items.length}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item) => (
          <DropdownMenuItem key={item.id} onSelect={item.onSelect}>
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
